/**
 * UK self-employed tax estimate (England, Wales & Northern Ireland bands).
 * Scottish income tax bands are not modelled.
 */

export interface TaxRates {
  personalAllowance: number;
  /** Income above this reduces the personal allowance by £1 for every £2. */
  allowanceTaperThreshold: number;
  /** Width of the 20% band, measured from the end of the personal allowance. */
  basicRateBand: number;
  /** Taxable income above this is charged at the additional rate. */
  additionalRateThreshold: number;
  basicRate: number;
  higherRate: number;
  additionalRate: number;
  class4NI: {
    lowerProfitsLimit: number;
    upperProfitsLimit: number;
    mainRate: number;
    upperRate: number;
  };
  /** Claimed instead of actual expenses when it is the larger deduction. */
  tradingAllowance: number;
}

export interface TaxInput {
  taxYear: string;
  /** Gross self-employed earnings for the tax year. */
  earnings: number;
  /** Total allowable expenses logged for the tax year. */
  expenses: number;
  hasOtherJob: boolean;
  /** Annual income from the other job. */
  otherJobSalary: number;
  /** The other job is employment taxed through PAYE (rather than more self-employment). */
  isPaye: boolean;
}

export interface TaxCalculationResult {
  annualEarnings: number;
  /** Expenses actually logged (money that has left your pocket). */
  expensesLogged: number;
  /** The deduction used against earnings: logged expenses or the trading allowance. */
  deduction: number;
  deductionType: 'expenses' | 'trading-allowance' | 'none';
  /** Taxable profit after the deduction. */
  profit: number;
  incomeTax: number;
  nationalInsurance: number;
  /** Earnings minus expenses paid minus tax and NI. */
  takeHomePay: number;
  /** How much less tax + NI is due because of the deduction. */
  taxSavedByDeduction: number;
}

const RATES_FROM_2024: TaxRates = {
  personalAllowance: 12570,
  allowanceTaperThreshold: 100000,
  basicRateBand: 37700,
  additionalRateThreshold: 125140,
  basicRate: 0.2,
  higherRate: 0.4,
  additionalRate: 0.45,
  class4NI: {
    lowerProfitsLimit: 12570,
    upperProfitsLimit: 50270,
    mainRate: 0.06,
    upperRate: 0.02,
  },
  tradingAllowance: 1000,
};

/**
 * Rates for a tax year ("2026-27"). Thresholds have been frozen since 2021-22 and
 * Class 4 NI has been 6% / 2% since 2024-25 (Class 2 is no longer compulsory),
 * so 2024-25 onwards share one table. Earlier years fall back to it as an approximation.
 */
export const getTaxYearRates = (taxYear: string): TaxRates => {
  const startYear = parseTaxYear(taxYear);
  if (startYear < 2024) {
    console.warn(`Tax rates for ${taxYear} aren't modelled; using 2024-25 onwards rates.`);
  }
  return RATES_FROM_2024;
};

const round2 = (n: number) => Math.round(n * 100) / 100;

/** Income tax on a person's total taxable income, including the allowance taper. */
const incomeTaxOn = (totalIncome: number, rates: TaxRates): number => {
  if (totalIncome <= 0) return 0;

  const taper = Math.max(0, (totalIncome - rates.allowanceTaperThreshold) / 2);
  const allowance = Math.max(0, rates.personalAllowance - taper);
  const taxable = Math.max(0, totalIncome - allowance);

  const basic = Math.min(taxable, rates.basicRateBand);
  const higher = Math.min(
    Math.max(0, taxable - rates.basicRateBand),
    rates.additionalRateThreshold - rates.basicRateBand
  );
  const additional = Math.max(0, taxable - rates.additionalRateThreshold);

  return basic * rates.basicRate + higher * rates.higherRate + additional * rates.additionalRate;
};

/** Class 4 National Insurance on self-employed profits. */
const class4On = (profits: number, rates: TaxRates): number => {
  const { lowerProfitsLimit, upperProfitsLimit, mainRate, upperRate } = rates.class4NI;
  const main = Math.min(Math.max(0, profits - lowerProfitsLimit), upperProfitsLimit - lowerProfitsLimit);
  const upper = Math.max(0, profits - upperProfitsLimit);
  return main * mainRate + upper * upperRate;
};

/**
 * Tax and NI attributable to this self-employment: the extra bill it creates on
 * top of whatever the other income already costs. Other income uses up the
 * personal allowance and lower bands first, so this profit is taxed at the
 * marginal rate. A non-PAYE other income is treated as more self-employed
 * profit, so it also shares the Class 4 NI bands.
 */
const taxAndNIOnProfit = (profit: number, input: TaxInput, rates: TaxRates) => {
  const other = input.hasOtherJob ? Math.max(0, input.otherJobSalary) : 0;

  const incomeTax = incomeTaxOn(profit + other, rates) - incomeTaxOn(other, rates);

  const otherClass4Profits = input.hasOtherJob && !input.isPaye ? other : 0;
  const nationalInsurance = class4On(profit + otherClass4Profits, rates) - class4On(otherClass4Profits, rates);

  return { incomeTax: Math.max(0, incomeTax), nationalInsurance: Math.max(0, nationalInsurance) };
};

export const calculateTaxYear = (input: TaxInput): TaxCalculationResult => {
  const rates = getTaxYearRates(input.taxYear);
  const earnings = Math.max(0, input.earnings);
  const expensesLogged = Math.max(0, input.expenses);

  // HMRC lets you claim either actual expenses or the £1,000 trading allowance, whichever is larger.
  const allowance = Math.min(rates.tradingAllowance, earnings);
  let deduction = 0;
  let deductionType: TaxCalculationResult['deductionType'] = 'none';
  if (expensesLogged > 0 || allowance > 0) {
    if (expensesLogged >= allowance) {
      deduction = expensesLogged;
      deductionType = 'expenses';
    } else {
      deduction = allowance;
      deductionType = 'trading-allowance';
    }
  }

  const profit = Math.max(0, earnings - deduction);
  const { incomeTax, nationalInsurance } = taxAndNIOnProfit(profit, input, rates);
  const withoutDeduction = taxAndNIOnProfit(earnings, input, rates);

  return {
    annualEarnings: round2(earnings),
    expensesLogged: round2(expensesLogged),
    deduction: round2(deduction),
    deductionType,
    profit: round2(profit),
    incomeTax: round2(incomeTax),
    nationalInsurance: round2(nationalInsurance),
    takeHomePay: round2(earnings - expensesLogged - incomeTax - nationalInsurance),
    taxSavedByDeduction: round2(
      withoutDeduction.incomeTax + withoutDeduction.nationalInsurance - incomeTax - nationalInsurance
    ),
  };
};

/**
 * Format tax year string (e.g., 2024 -> "2024-25")
 */
export const formatTaxYear = (startYear: number): string => {
  const endYear = startYear + 1;
  return `${startYear}-${endYear.toString().slice(-2)}`;
};

/**
 * Parse tax year string to start year (e.g., "2024-25" -> 2024)
 */
export const parseTaxYear = (taxYear: string): number => {
  return parseInt(taxYear.split('-')[0]);
};

export const getNextTaxYear = (taxYear: string): string => formatTaxYear(parseTaxYear(taxYear) + 1);

export const getPreviousTaxYear = (taxYear: string): string => formatTaxYear(parseTaxYear(taxYear) - 1);

/** UK tax year months in order: April (4) to March (3). */
export const TAX_YEAR_MONTHS = [4, 5, 6, 7, 8, 9, 10, 11, 12, 1, 2, 3] as const;

/** Position of a calendar month within the tax year (April = 0 … March = 11). */
export const taxYearMonthIndex = (month: number): number => (month >= 4 ? month - 4 : month + 8);

/** The calendar year a tax-year month falls in. */
export const calendarYearForMonth = (taxYearStart: number, month: number): number =>
  month >= 4 ? taxYearStart : taxYearStart + 1;
